import { SENSITIVE_ROLES, type Member, type Role } from '@sistemas/contracts'

import { describeDbError, getSupabase } from '@/lib/supabase'
import { todayLocalIso } from '@/lib/dates'
import type { MemberRoleRow } from '@/modules/projects/store'

/** Linha bruta de `sistemas.member` via PostgREST. */
interface MemberRow {
  id: string
  user_id: string | null
  email: string
  full_name: string
  github_login: string | null
  position_title: string | null
  manager_id: string | null
  hired_on: string | null
  terminated_on: string | null
  is_active: boolean
  weekly_capacity_hours: number | string | null
  avatar_url: string | null
  created_at: string
  updated_at: string
}

interface MemberRoleDbRow {
  member_id: string
  role: Role
  reason: string | null
  granted_at: string
}

export type PeopleWriteResult<T> = { ok: true; data: T } | { ok: false; reason: string }

function initialsFrom(name: string): string {
  const parts = name
    .trim()
    .split(/\s+/)
    .filter((p) => p.length > 0 && !/^(d[aeio]s?|e)$/i.test(p))
  if (parts.length === 0) return '?'
  if (parts.length === 1) return (parts[0]?.[0] ?? '?').toUpperCase()
  return `${parts[0]?.[0] ?? ''}${parts[parts.length - 1]?.[0] ?? ''}`.toUpperCase()
}

function toMember(row: MemberRow): Member {
  const capacity =
    row.weekly_capacity_hours === null || row.weekly_capacity_hours === undefined
      ? undefined
      : Number(row.weekly_capacity_hours)

  return {
    id: row.id,
    ...(row.user_id ? { userId: row.user_id } : {}),
    fullName: row.full_name,
    email: row.email,
    initials: initialsFrom(row.full_name),
    isActive: row.is_active,
    ...(row.position_title ? { positionTitle: row.position_title } : {}),
    ...(row.manager_id ? { managerId: row.manager_id } : {}),
    ...(row.github_login ? { githubLogin: row.github_login } : {}),
    ...(capacity !== undefined && !Number.isNaN(capacity)
      ? { weeklyCapacityHours: capacity }
      : {}),
    ...(row.hired_on ? { startedOn: row.hired_on } : {}),
    ...(row.terminated_on ? { deactivatedAt: row.terminated_on } : {}),
    ...(row.avatar_url ? { avatarPath: row.avatar_url } : {}),
  }
}

function toRoleRow(row: MemberRoleDbRow): MemberRoleRow {
  return {
    memberId: row.member_id,
    role: row.role,
    grantedAt: row.granted_at,
    ...(row.reason ? { reason: row.reason } : {}),
  }
}

function dbError(error: { message?: string } | null, fallback: string): string {
  return describeDbError(error, fallback)
}

/** Lista membros + papéis do schema `sistemas`. */
export async function fetchPeopleCatalog(): Promise<
  PeopleWriteResult<{ members: Member[]; memberRoles: MemberRoleRow[] }>
> {
  const sb = getSupabase()
  const [membersRes, rolesRes] = await Promise.all([
    sb.from('member').select('*').order('full_name', { ascending: true }),
    sb.from('member_role').select('member_id, role, reason, granted_at'),
  ])

  if (membersRes.error) {
    return { ok: false, reason: dbError(membersRes.error, 'Falha ao carregar pessoas.') }
  }
  if (rolesRes.error) {
    return { ok: false, reason: dbError(rolesRes.error, 'Falha ao carregar papéis.') }
  }

  return {
    ok: true,
    data: {
      members: ((membersRes.data ?? []) as MemberRow[]).map(toMember),
      memberRoles: ((rolesRes.data ?? []) as MemberRoleDbRow[]).map(toRoleRow),
    },
  }
}

/** Um usuário do SSO, como o seletor do cadastro o mostra. */
export interface SsoUser {
  readonly userId: string
  readonly email: string
  readonly displayName: string | null
  /** `active` já logou; `invited` tem conta e ainda não entrou. */
  readonly status: 'active' | 'invited'
  /** Ficha do Hub já vinculada a este usuário, quando houver. */
  readonly memberId: string | null
  readonly memberName: string | null
}

interface SsoUserRow {
  user_id: string
  email: string
  display_name: string | null
  status: 'active' | 'invited'
  member_id: string | null
  member_name: string | null
}

/**
 * Diretório do SSO para o seletor. Gate no banco (`can_manage_people`): quem
 * não é gestão recebe recusa, não uma lista vazia.
 */
export async function listSsoUsers(search: string): Promise<PeopleWriteResult<SsoUser[]>> {
  const sb = getSupabase()
  const { data, error } = await sb.rpc('list_sso_users', { p_search: search.trim() || null })
  if (error) {
    return { ok: false, reason: dbError(error, 'Não foi possível consultar os usuários do SSO.') }
  }
  return {
    ok: true,
    data: ((data ?? []) as SsoUserRow[]).map((r) => ({
      userId: r.user_id,
      email: r.email,
      displayName: r.display_name,
      status: r.status,
      memberId: r.member_id,
      memberName: r.member_name,
    })),
  }
}

/**
 * Cria a pessoa a partir de um usuário do SSO (migration 022).
 *
 * Uma chamada só: ficha e papel mínimo entram na mesma transação, e a
 * membership do portal vem pelo trigger. O e-mail NÃO é parâmetro — vem sempre
 * do SSO; o digitado à mão foi o que deixou gente do time de fora.
 */
export async function createMemberFromSso(input: {
  userId: string
  fullName?: string
  positionTitle?: string
  managerId?: string
  githubLogin?: string
  weeklyCapacityHours?: number
  startedOn?: string
}): Promise<PeopleWriteResult<Member>> {
  const sb = getSupabase()
  const { data, error } = await sb.rpc('create_member_from_sso', {
    p_user_id: input.userId,
    p_full_name: input.fullName?.trim() || null,
    p_position_title: input.positionTitle?.trim() || null,
    p_manager_id: input.managerId?.trim() || null,
    p_github_login: input.githubLogin?.trim() || null,
    p_weekly_capacity_hours: input.weeklyCapacityHours ?? null,
    p_hired_on: input.startedOn || null,
  })
  if (error || !data) {
    return { ok: false, reason: dbError(error, 'Não foi possível cadastrar a pessoa.') }
  }
  return { ok: true, data: toMember(data as MemberRow) }
}

/**
 * Vincula uma ficha antiga (digitada à mão, sem login) a um usuário do SSO.
 * O banco adota o e-mail do SSO e recusa trocar o dono de ficha já vinculada.
 */
export async function linkMemberSso(
  memberId: string,
  userId: string,
): Promise<PeopleWriteResult<Member>> {
  const sb = getSupabase()
  const { data, error } = await sb.rpc('link_member_sso', {
    p_member_id: memberId,
    p_user_id: userId,
  })
  if (error || !data) {
    return { ok: false, reason: dbError(error, 'Não foi possível vincular a pessoa ao SSO.') }
  }
  return { ok: true, data: toMember(data as MemberRow) }
}

export async function patchMember(
  id: string,
  patch: {
    fullName?: string
    email?: string
    positionTitle?: string | undefined
    managerId?: string | null
    githubLogin?: string | undefined
    weeklyCapacityHours?: number | undefined
    startedOn?: string | undefined
  },
): Promise<PeopleWriteResult<Member>> {
  const sb = getSupabase()
  const payload: Record<string, unknown> = {}

  if (patch.fullName !== undefined) payload.full_name = patch.fullName.trim()
  if (patch.email !== undefined) payload.email = patch.email.trim().toLowerCase()
  if (patch.positionTitle !== undefined) {
    payload.position_title = patch.positionTitle.trim() || null
  }
  if (patch.managerId !== undefined) {
    payload.manager_id = patch.managerId === null ? null : patch.managerId
  }
  if (patch.githubLogin !== undefined) {
    payload.github_login = patch.githubLogin.trim() || null
  }
  if (patch.weeklyCapacityHours !== undefined) {
    payload.weekly_capacity_hours = patch.weeklyCapacityHours
  }
  if (patch.startedOn !== undefined) {
    payload.hired_on = patch.startedOn || null
  }

  const { data, error } = await sb.from('member').update(payload).eq('id', id).select('*').single()
  if (error || !data) {
    return { ok: false, reason: dbError(error, 'Não foi possível salvar a pessoa.') }
  }
  return { ok: true, data: toMember(data as MemberRow) }
}

/** Q48 — soft-delete: desativa e registra terminated_on. */
export async function softDeleteMember(id: string): Promise<PeopleWriteResult<Member>> {
  const sb = getSupabase()
  const today = todayLocalIso()
  const { data, error } = await sb
    .from('member')
    .update({ is_active: false, terminated_on: today })
    .eq('id', id)
    .select('*')
    .single()

  if (error || !data) {
    return { ok: false, reason: dbError(error, 'Não foi possível desativar a pessoa.') }
  }
  return { ok: true, data: toMember(data as MemberRow) }
}

export async function reactivateMemberRemote(id: string): Promise<PeopleWriteResult<Member>> {
  const sb = getSupabase()
  const { data, error } = await sb
    .from('member')
    .update({ is_active: true, terminated_on: null })
    .eq('id', id)
    .select('*')
    .single()

  if (error || !data) {
    return { ok: false, reason: dbError(error, 'Não foi possível reativar a pessoa.') }
  }
  return { ok: true, data: toMember(data as MemberRow) }
}

export async function replaceMemberRoles(
  memberId: string,
  roles: Role[],
  reason?: string,
): Promise<PeopleWriteResult<MemberRoleRow[]>> {
  const sb = getSupabase()

  const { data: existing, error: existingError } = await sb
    .from('member_role')
    .select('member_id, role, reason, granted_at')
    .eq('member_id', memberId)

  if (existingError) {
    return { ok: false, reason: dbError(existingError, 'Falha ao ler papéis atuais.') }
  }

  const current = (existing ?? []) as MemberRoleDbRow[]
  const currentSet = new Set(current.map((r) => r.role))
  const nextSet = new Set(roles)

  const toRemove = current.filter((r) => !nextSet.has(r.role)).map((r) => r.role)
  const toAdd = roles.filter((r) => !currentSet.has(r))

  if (toRemove.length > 0) {
    const { error } = await sb
      .from('member_role')
      .delete()
      .eq('member_id', memberId)
      .in('role', toRemove)
    if (error) {
      return { ok: false, reason: dbError(error, 'Falha ao remover papéis.') }
    }
  }

  if (toAdd.length > 0) {
    const rows = toAdd.map((role) => ({
      member_id: memberId,
      role,
      // Espelha o trigger `member_role_require_reason`: papel sensível sem
      // justificativa é recusado pelo banco, não só desencorajado pela UI.
      ...(SENSITIVE_ROLES.includes(role) ? { reason: reason?.trim() || null } : {}),
    }))
    const { error } = await sb.from('member_role').insert(rows)
    if (error) {
      return { ok: false, reason: dbError(error, 'Falha ao conceder papéis.') }
    }
  }

  const { data: refreshed, error: refreshError } = await sb
    .from('member_role')
    .select('member_id, role, reason, granted_at')
    .eq('member_id', memberId)

  if (refreshError || !refreshed) {
    return { ok: false, reason: dbError(refreshError, 'Papéis gravados, mas a leitura falhou.') }
  }

  return { ok: true, data: (refreshed as MemberRoleDbRow[]).map(toRoleRow) }
}
